import { Box, Skeleton, Stack, Typography } from '@mui/material';
import { useCallback } from 'react';

import { Categories, LocalCartItem, type Ticket } from 'restful/tickets';
import { TicketStatusFragment } from 'types/api/dato-operations.types';

import TicketInquiryItem from './TicketInquiryItem';
import TicketItem from './TicketItem';
import TicketSeatingItem from './TicketSeatingItem';

interface Props {
  isFilterActive: boolean;
  categories: Categories;
  eventTicketStatus: Array<TicketStatusFragment>;
  isLoadingOffers: boolean;
  cartItems: Array<LocalCartItem>;
  onSelectSeatingForTicket: (ticket: Ticket) => void;
  onAddItem: (ticket: Ticket) => void;
  onRemoveItem: (ticket: Ticket) => void;
  onOpenDetail: (ticket: Ticket) => void;
}

const TicketList = ({
  eventTicketStatus,
  isFilterActive,
  categories,
  isLoadingOffers,
  cartItems,
  onAddItem,
  onRemoveItem,
  onSelectSeatingForTicket,
  onOpenDetail,
}: Props) => {
  const getCartItem = useCallback(
    (ticketId: string) => {
      return cartItems.find(
        ({ catalogueItemId }) => catalogueItemId === ticketId,
      );
    },
    [cartItems],
  );

  const getTicketStatus = useCallback(
    (ticketId: string) => {
      return eventTicketStatus.find((item) => item.ticketId === ticketId);
    },
    [eventTicketStatus],
  );

  return (
    <Stack
      sx={{
        px: { xs: 2, md: 0 },
        gap: 3,
        mb: 2,
      }}
    >
      {isLoadingOffers && (
        <div>
          <Skeleton
            variant="rectangular"
            sx={{ mb: 1, height: { xs: 27, md: 30 } }}
          />
          <Skeleton variant="rounded" height={74} />
        </div>
      )}

      {categories.map((category) => (
        <Box
          key={category.id}
          id={category.id}
          sx={{ scrollMarginTop: { xs: 88, md: 72 } }}
        >
          {(categories.length > 1 || isFilterActive) && (
            <Typography variant="h7" sx={{ mb: 1 }}>
              {category.categoryWithSubcategoryName}
            </Typography>
          )}
          <Stack sx={{ gap: 1 }}>
            {category.tickets.map((ticket) =>
              ticket.isExternalForm ? (
                <TicketInquiryItem
                  key={ticket.id}
                  ticket={ticket}
                  status={getTicketStatus(ticket.id)}
                  onOpenDetail={onOpenDetail}
                />
              ) : !!ticket.seatingData?.seatingChartEventOrSeasonId ||
                !!ticket.seatingData?.seatingCategoryKey ? (
                <TicketSeatingItem
                  key={ticket.id}
                  ticket={ticket}
                  status={getTicketStatus(ticket.id)}
                  cartItem={getCartItem(ticket.id)}
                  onSelectSeats={onSelectSeatingForTicket}
                  onOpenDetail={onOpenDetail}
                />
              ) : (
                <TicketItem
                  key={ticket.id}
                  ticket={ticket}
                  status={getTicketStatus(ticket.id)}
                  cartItem={getCartItem(ticket.id)}
                  onAddItem={onAddItem}
                  onRemoveItem={onRemoveItem}
                  onOpenDetail={onOpenDetail}
                />
              ),
            )}
          </Stack>
        </Box>
      ))}
    </Stack>
  );
};

export default TicketList;
